import type { TowerDef, TowerId } from "../types";

/** Tower definitions — base (1 element) and fusion (2 elements). */
export const TOWERS: Record<TowerId, TowerDef> = {
  // ---------- Base (1 element) ----------
  fire: {
    id: "fire",
    label: "Lina",
    recipe: ["fire"],
    cost: 50,
    range: 130,
    damage: 14,
    rate: 1.0,
    color: 0xff5a3a,
    splash: 0,
    crit: { chance: 0.18, mult: 2.0 },
    description: "Strong single-target burst.",
  },
  water: {
    id: "water",
    label: "Rylai",
    recipe: ["water"],
    cost: 50,
    range: 120,
    damage: 9,
    rate: 1.6,
    color: 0x4cc9f0,
    splash: 0,
    slow: { factor: 0.6, duration: 1200 },
    description: "Chilling wave — slows on hit.",
  },
  earth: {
    id: "earth",
    label: "Zharvakko",
    recipe: ["earth"],
    cost: 60,
    range: 100,
    damage: 18,
    rate: 0.7,
    color: 0xb08a4f,
    splash: 32,
    description: "Slow but small AOE.",
  },
  nature: {
    id: "nature",
    label: "Mirana",
    recipe: ["nature"],
    cost: 50,
    range: 130,
    damage: 5,
    rate: 1.4,
    color: 0x7be36b,
    splash: 0,
    dot: { dps: 8, duration: 2000 },
    description: "Poison damage over time.",
  },

  // ---------- Fusion (2 elements) ----------
  steam: {
    id: "steam",
    label: "Mangix",
    recipe: ["fire", "water"],
    cost: 150,
    range: 130,
    damage: 12,
    rate: 1.4,
    color: 0xb6e6ff,
    splash: 40,
    slow: { factor: 0.72, duration: 900 },
    description: "Hot mist — splash + light chill.",
  },
  lava: {
    id: "lava",
    label: "Karroch",
    recipe: ["fire", "earth"],
    cost: 180,
    range: 140,
    damage: 14,
    rate: 0.9,
    color: 0xff6620,
    splash: 32,
    description: "Beastmaster — twin Wild Axes spin & cleave.",
  },
  forest_fire: {
    id: "forest_fire",
    label: "Sven",
    recipe: ["fire", "nature"],
    cost: 160,
    range: 150,
    damage: 18,
    rate: 1.2,
    color: 0xb84aff,
    splash: 30,
    dot: { dps: 12, duration: 2500 },
    description: "Void blade — purple spinning saw, anti-swarm.",
  },
  mud: {
    id: "mud",
    label: "Raigor",
    recipe: ["water", "earth"],
    cost: 140,
    range: 120,
    damage: 16,
    rate: 1.0,
    color: 0x7a5a3a,
    splash: 36,
    slow: { factor: 0.62, duration: 1300 },
    description: "Heavy bone axe — AOE + mire slow.",
  },
  ice: {
    id: "ice",
    label: "Auroth",
    recipe: ["water", "nature"],
    cost: 160,
    range: 130,
    damage: 22,
    rate: 1.3,
    color: 0xa6e3ff,
    splash: 0,
    slow: { factor: 0.45, duration: 1600 },
    crit: { chance: 0.12, mult: 2.0 },
    description: "Razor snowflake — deep freeze.",
  },
  vine: {
    id: "vine",
    label: "Lesale",
    recipe: ["earth", "nature"],
    cost: 170,
    range: 110,
    damage: 26,
    rate: 0.9,
    color: 0x4ea84a,
    splash: 0,
    dot: { dps: 6, duration: 2000 },
    description: "Thorn whip — heavy hit + bleed.",
  },

  // ---------- Triple fusion (3 elements) ----------
  brew: {
    id: "brew",
    label: "Rattletrap",
    recipe: ["fire", "water", "earth"],
    cost: 250,
    range: 130,
    damage: 30,
    rate: 1.3,
    color: 0xd4884a,
    splash: 50,
    dot: { dps: 10, duration: 1500 },
    description: "Drunken brawler — fire ale splash + burn.",
  },
  voidmage: {
    id: "voidmage",
    label: "Zeus",
    recipe: ["fire", "water", "nature"],
    cost: 270,
    range: 160,
    damage: 30,
    rate: 1.4,
    color: 0xffd84a,
    splash: 25,
    dot: { dps: 14, duration: 1800 },
    description: "Zeus — chain lightning AOE.",
  },
  beast: {
    id: "beast",
    label: "Vrogros",
    recipe: ["fire", "earth", "nature"],
    cost: 290,
    range: 130,
    damage: 45,
    rate: 0.85,
    color: 0x8a5a28,
    splash: 45,
    dot: { dps: 8, duration: 2000 },
    description: "Underlord — abyssal smash + lava bleed.",
  },
  tide: {
    id: "tide",
    label: "Slardar",
    recipe: ["water", "earth", "nature"],
    cost: 280,
    range: 115,
    damage: 50,
    rate: 0.95,
    color: 0x2a8a6a,
    splash: 65,
    slow: { factor: 0.55, duration: 1500 },
    description: "Ravage tentacle — huge AOE + slow.",
  },

  // ---------- Mythic (4 elements — all required) ----------
  necro: {
    id: "necro",
    label: "Rotund'jere",
    recipe: ["fire", "water", "earth", "nature"],
    cost: 420,
    range: 145,
    damage: 60,
    rate: 1.0,
    color: 0x6aff8a,
    splash: 55,
    dot: { dps: 18, duration: 2200 },
    description: "Necrolyte — death pulse + heartstopper decay.",
  },
  phantom: {
    id: "phantom",
    label: "Mortred",
    recipe: ["fire", "water", "earth", "nature"],
    cost: 440,
    range: 175,
    damage: 110,
    rate: 0.7,
    color: 0xd4a0ff,
    splash: 0,
    crit: { chance: 0.45, mult: 3.5 },
    description: "Phantom Assassin — Coup de Grâce single-target crit.",
  },
  luna: {
    id: "luna",
    label: "Luna",
    recipe: ["fire", "water", "earth", "nature"],
    cost: 460,
    range: 170,
    damage: 38,
    rate: 1.6,
    color: 0xc8d8ff,
    splash: 30,
    crit: { chance: 0.28, mult: 2.2 },
    description: "Moon Glaive — long-range bouncing strike.",
  },
  tinker: {
    id: "tinker",
    label: "Boush",
    recipe: ["fire", "water", "earth", "nature"],
    cost: 480,
    range: 180,
    damage: 160,
    rate: 0.55,
    color: 0xff8a3a,
    splash: 90,
    crit: { chance: 0.15, mult: 2.0 },
    description: "Tinker — heavy bomb lob, devastating splash.",
  },
};

export const TOWER_LIST: TowerDef[] = Object.values(TOWERS);
